import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters('companyModule', ['currentCompany']),

    company() {
      return this.currentCompany || {};
    },

    companyInitials() {
      const name = this.company.shortName || this.company.name || '';
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('') || 'CO';
    },

    companyAddress() {
      const c = this.company;
      if (!c) return '';
      return [c.street, c.city, c.province, c.country].filter(Boolean).join(', ');
    },
  },

  methods: {
    getBasePrintStyles() {
      return `
        <style>
          @page { margin: 16mm 16mm 18mm; }
          * { box-sizing: border-box; }
          html, body { padding: 0; margin: 0; }
          body {
            font-family: 'Segoe UI', Helvetica, Arial, sans-serif;
            color: #2b2b2b;
            font-size: 11.5px;
            line-height: 1.5;
          }
          .dim { color: #8a8a8a; }
          .cap { text-transform: capitalize; }
          .num { font-variant-numeric: tabular-nums; }
          .divider-heavy { height: 2px; background: #14243A; margin-bottom: 10px; }

          .letterhead { margin-bottom: 8px; page-break-inside: avoid; page-break-after: avoid; }
          .letterhead-top {
            display: flex; align-items: center; justify-content: space-between;
            border-bottom: 2px solid #14243A; padding-bottom: 14px; margin-bottom: 18px;
          }
          .brand { display: flex; align-items: center; gap: 10px; }
          .brand-mark {
            width: 32px; height: 32px; border: 1px solid #14243A; color: #14243A;
            display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 12px;
            flex-shrink: 0;
          }
          .brand-logo { width: 32px; height: 32px; object-fit: contain; flex-shrink: 0; }
          .brand-name { font-family: Georgia, 'Times New Roman', serif; font-size: 14px; color: #14243A; }
          .brand-address { font-size: 10.5px; color: #8a8a8a; margin-top: 1px; }
          .doc-meta { text-align: right; font-size: 10.5px; color: #8a8a8a; }
          .doc-ref { font-size: 9.5px; text-transform: uppercase; letter-spacing: .08em; color: #b0b0ac; margin-bottom: 2px; }

          .doc-title-block { text-align: center; border-bottom: 0.5px solid #e2e0d8; padding-bottom: 16px; margin-bottom: 24px; }
          .doc-title { font-family: Georgia, 'Times New Roman', serif; font-size: 19px; color: #14243A; margin-bottom: 4px; }
          .doc-subtitle { font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; color: #9a9a9a; }

          .section { margin-bottom: 22px; page-break-inside: avoid; }
          .section-head {
            display: flex; align-items: baseline; gap: 8px;
            border-bottom: 0.5px solid #d8d6cc; padding-bottom: 6px; margin-bottom: 10px;
            page-break-after: avoid;
          }
          .section-num { font-size: 10px; letter-spacing: .06em; color: #8a8a8a; }
          .section-title { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #5a5850; }
          .badge { margin-left: auto; }
          .subhead { font-size: 10.5px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin: 12px 0 8px; }
          .empty { font-size: 11.5px; color: #9a9a9a; font-style: italic; padding: 4px 0; }
          .footnote { font-size: 10.5px; color: #9a9a9a; margin-top: 8px; }
          .note { font-size: 11px; color: #5a5850; background: #f9f8f4; border-left: 2px solid #d8d6cc; padding: 6px 10px; margin-top: 6px; }

          table { width: 100%; border-collapse: collapse; font-size: 11.5px; margin-bottom: 2px; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
          th, td {
            border-bottom: 0.5px solid #edece5; padding: 7px 10px; text-align: left; vertical-align: middle;
            word-wrap: break-word; overflow-wrap: break-word;
          }
          th {
            background: #f9f8f4; font-size: 9.5px; font-weight: 700; color: #9a9a9a;
            text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #d8d6cc;
          }
          .label {
            font-size: 10.5px; font-weight: 700; color: #9a9a9a; text-transform: uppercase;
            letter-spacing: .05em; white-space: nowrap; width: 190px;
          }
          table.tight td { padding: 6px 10px; }

          .pill {
            display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 10.5px;
            font-weight: 600; white-space: nowrap;
          }

          .score-row { display: flex; gap: 20px; margin-bottom: 4px; page-break-inside: avoid; }
          .score-box {
            text-align: center; min-width: 92px; border: 0.5px solid #d8d6cc;
            padding: 10px 14px; display: flex; flex-direction: column; justify-content: center;
          }
          .score-num { font-family: Georgia, 'Times New Roman', serif; font-size: 26px; color: #14243A; }
          .score-level { font-size: 9.5px; text-transform: uppercase; letter-spacing: .06em; margin-top: 2px; }

          /* Executive stat grid (ReportStatGrid print counterpart) */
          .stat-grid { display: flex; flex-wrap: wrap; gap: 0; border: 1px solid #e2e6ec; margin-bottom: 22px; page-break-inside: avoid; }
          .stat-card { flex: 1 1 0; min-width: 110px; padding: 12px 16px; border-right: 1px solid #e2e6ec; background: #f9fafc; }
          .stat-card:last-child { border-right: none; }
          .stat-label { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #9aa1ab; margin-bottom: 6px; }
          .stat-value { font-size: 20px; font-family: Georgia, 'Times New Roman', serif; }

          /* Distribution bars (ReportDistribution print counterpart) */
          .dist-row { margin-bottom: 14px; page-break-inside: avoid; }
          .dist-row:last-child { margin-bottom: 0; }
          .dist-row-top { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 5px; }
          .dist-label { font-weight: 700; text-transform: capitalize; }
          .dist-count { color: #6b7280; }
          .bar-track { height: 7px; border-radius: 6px; background: #edf0f4; overflow: hidden; }
          .bar-fill { height: 100%; border-radius: 6px; }

          /* Subblock — bordered sub-record card nested in a section */
          .subblock { border: 0.5px solid #d8d6cc; margin-bottom: 10px; page-break-inside: avoid; }
          .subblock:last-child { margin-bottom: 0; }
          .subblock-header {
            display: flex; align-items: center; justify-content: space-between; gap: 10px;
            padding: 7px 10px; background: #f9f8f4; border-bottom: 0.5px solid #d8d6cc;
          }
          .subblock-title { font-size: 11px; font-weight: 700; color: #14243A; }
          .subblock-body table td.label { width: 150px; }

          /* Sign-off / certification block */
          .signoff { font-size: 11.5px; color: #444; line-height: 1.6; }
          .signoff p { margin: 0 0 8px; }
          .sign-row { display: flex; gap: 20px; margin-top: 24px; }
          .sign-block { flex: 1; }
          .sign-line { border-bottom: 0.5px solid #333; margin-bottom: 4px; height: 22px; }
          .sign-label { font-size: 9.5px; color: #9a9a9a; text-align: center; }

          .doc-footer { margin-top: 26px; page-break-inside: avoid; }
          .doc-footer-row { display: flex; justify-content: space-between; font-size: 10px; color: #9a9a9a; margin-top: 10px; }
          .doc-footer-confidential { font-size: 9.5px; color: #b0b0ac; margin-top: 6px; text-align: center; }
        </style>
      `;
    },

    // ── Reusable print HTML fragments ──
    buildPrintLetterheadHtml({ title, subtitle, refLabel }) {
      const e = this.escapeHtml.bind(this);
      return `
        <div class="letterhead">
          <div class="letterhead-top">
            <div class="brand">
              ${this.company.logo
                ? `<img src="${e(this.company.logo)}" class="brand-logo" />`
                : `<div class="brand-mark">${e(this.companyInitials)}</div>`}
              <div>
                <div class="brand-name">${e(this.company.name || 'Company name')}</div>
                <div class="brand-address">${e(this.companyAddress || 'Address not on file')}</div>
              </div>
            </div>
            <div class="doc-meta">
              ${refLabel ? `<div class="doc-ref">${e(refLabel)}</div>` : ''}
              <div class="dim">${e(this.formatDate(new Date()))}</div>
            </div>
          </div>
          <div class="doc-title-block">
            <div class="doc-title">${e(title)}</div>
            ${subtitle ? `<div class="doc-subtitle">${e(subtitle)}</div>` : ''}
          </div>
        </div>
      `;
    },

    buildPrintFooterHtml() {
      const e = this.escapeHtml.bind(this);
      return `
        <div class="doc-footer">
          <div class="divider-heavy"></div>
          <div class="doc-footer-row">
            <span>Generated ${e(this.formatDateTime(new Date()))} &middot; for internal use only</span>
            <span>${e(this.company.name || 'Company name')} &copy; ${new Date().getFullYear()}</span>
          </div>
          <div class="doc-footer-confidential">
            This document contains confidential customer information and is intended solely for authorized personnel. Unauthorized distribution is strictly prohibited.
          </div>
        </div>
      `;
    },

    // number + title + optional badge wrapper
    buildSectionHtml({ number, title, badgeHtml = '', bodyHtml }) {
      const e = this.escapeHtml.bind(this);
      return `
        <div class="section">
          <div class="section-head">
            <span class="section-num">${e(number)}</span>
            <span class="section-title">${e(title)}</span>
            ${badgeHtml}
          </div>
          ${bodyHtml}
        </div>
      `;
    },

    // stats: [{ label, value, tone }]
    buildStatGridHtml(stats) {
      const e = this.escapeHtml.bind(this);
      const cards = stats.map(s => `
        <div class="stat-card">
          <div class="stat-label">${e(s.label)}</div>
          <div class="stat-value" style="color:${this.toneTextColor(s.tone)}">${e(typeof s.value === 'number' ? s.value.toLocaleString() : s.value)}</div>
        </div>`).join('');
      return `<div class="stat-grid">${cards}</div>`;
    },

    // items: [{ label, count, color }]
    buildDistributionHtml(items, total) {
      const e = this.escapeHtml.bind(this);
      if (!items.length) return `<div class="empty">No data available.</div>`;
      const effectiveTotal = total || items.reduce((s, i) => s + i.count, 0) || 1;
      return items.map(item => {
        const pct = Math.round((item.count / effectiveTotal) * 100);
        const color = item.color || '#14243A';
        return `
          <div class="dist-row">
            <div class="dist-row-top">
              <span class="dist-label" style="color:${color}">${e(item.label)}</span>
              <span class="dist-count">${e(item.count)} <span class="dim">(${pct}%)</span></span>
            </div>
            <div class="bar-track"><div class="bar-fill" style="width:${pct}%;background:${color}"></div></div>
          </div>`;
      }).join('');
    },

    // a bordered sub-record card inside a section
    buildSubblockHtml({ title, badgeHtml = '', bodyHtml }) {
      const e = this.escapeHtml.bind(this);
      return `
        <div class="subblock">
          <div class="subblock-header">
            <span class="subblock-title">${e(title)}</span>
            ${badgeHtml}
          </div>
          <div class="subblock-body">${bodyHtml}</div>
        </div>
      `;
    },

    printHtml({ title, bodyHtml, extraStyles = '' }) {
      const styles = this.getBasePrintStyles() + extraStyles;

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      const printDoc = iframe.contentWindow.document;
      printDoc.open();
      printDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.escapeHtml(title)}</title>
            ${styles}
          </head>
          <body>${bodyHtml}</body>
        </html>
      `);
      printDoc.close();

      const triggerPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };

      if (printDoc.readyState === 'complete') {
        triggerPrint();
      } else {
        iframe.onload = triggerPrint;
      }

      iframe.contentWindow.onafterprint = cleanup;
      setTimeout(cleanup, 30000);
    },
  },
};